import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Types for the optimized navigation data
export interface GoalProjectNavigationData {
  project: {
    id: string;
    title: string;
    created_at: string;
    updated_at: string;
  };
  pages: Array<{
    id: string;
    title: string;
    created_at: string;
    updated_at: string;
  }>;
}

export interface ProjectNavigationData {
  project: {
    id: string;
    title: string;
    created_at: string;
    updated_at: string;
    is_favorite: boolean;
    is_archived: boolean;
    metadata: any;
    settings?: any;  // ADDED: Include settings
  };
  goal: {
    id: string;
    title: string;
    description: string;
    created_at: string;
    openai_thread_id: string | null;  // ADDED: Include goal's thread ID
  } | null;
  pages: Array<{
    id: string;
    title: string;
    created_at: string;
    updated_at: string;
    type: string;
  }>;
}

/**
 * Optimized hook for Goal page navigation
 * Replaces useGoalWorkbook + usePageContentQuery (2 calls → 1 call)
 */
export function useGoalProjectNavigation(goalId: string) {
  return useQuery({
    queryKey: ['goal-project-navigation', goalId],
    queryFn: async (): Promise<GoalProjectNavigationData | null> => {
      if (!goalId) return null;

      const { data, error } = await supabase.rpc('get_goal_project_navigation', {
        p_goal_id: goalId
      });

      if (error) {
        console.error('Error fetching goal project navigation:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        return null;
      }

      // Transform the flat response into structured data
      const firstRow = data[0];
      const project = {
        id: firstRow.project_id,
        title: firstRow.project_title,
        created_at: firstRow.project_created_at,
        updated_at: firstRow.project_updated_at,
      };

      // Extract unique pages (remove duplicates from JOIN)
      const pagesMap = new Map();
      data.forEach(row => {
        if (row.page_id && !pagesMap.has(row.page_id)) {
          pagesMap.set(row.page_id, {
            id: row.page_id,
            title: row.page_title,
            created_at: row.page_created_at,
            updated_at: row.page_updated_at,
          });
        }
      });

      // Sort by updated_at DESC (most recently updated first)
      const pages = Array.from(pagesMap.values())
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at));

      return { project, pages };
    },
    enabled: !!goalId,
  });
}

/**
 * Optimized hook for Project Manager page navigation
 * Replaces usePageQuery + useWorkbookGoal + usePageContentQuery (3 calls → 1 call)
 */
export function useProjectNavigation(projectId: string) {
  return useQuery({
    queryKey: ['project-navigation', projectId],
    queryFn: async (): Promise<ProjectNavigationData | null> => {
      if (!projectId) return null;

      const { data, error } = await supabase.rpc('get_project_navigation', {
        p_project_id: projectId
      });

      if (error) {
        console.error('Error fetching project navigation:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        return null;
      }

      // Transform the flat response into structured data
      const firstRow = data[0];
      
      // Debug: Log what we're getting from the database
      console.log('RPC Response firstRow:', firstRow);
      console.log('Project Settings from RPC:', (firstRow as any).project_settings);
      const project = {
        id: firstRow.project_id,
        title: firstRow.project_title,
        created_at: firstRow.project_created_at,
        updated_at: firstRow.project_updated_at,
        is_favorite: firstRow.project_is_favorite,
        is_archived: firstRow.project_is_archived,
        metadata: firstRow.project_metadata,
        settings: (firstRow as any).project_settings,  // ADDED: Include settings
      };

      // Extract goal (only one per project)
      const goal = firstRow.goal_id ? {
        id: firstRow.goal_id,
        title: firstRow.goal_title,
        description: firstRow.goal_description,
        created_at: firstRow.goal_created_at,
        openai_thread_id: (firstRow as any).goal_openai_thread_id,  // Use type assertion until types are updated
      } : null;

      // Extract unique pages (remove duplicates from JOIN)
      const pagesMap = new Map();
      data.forEach(row => {
        if (row.page_id && !pagesMap.has(row.page_id)) {
          pagesMap.set(row.page_id, {
            id: row.page_id,
            title: row.page_title,
            created_at: row.page_created_at,
            updated_at: row.page_updated_at,
            type: row.page_type,
          });
        }
      });

      // Sort by updated_at DESC (most recently updated first)
      const pages = Array.from(pagesMap.values())
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at));

      return { project, goal, pages };
    },
    enabled: !!projectId,
  });
}

/**
 * Backward compatibility helpers - these provide the same interface as the old hooks
 * but use the optimized data internally
 */

export function useOptimizedWorkbookGoal(projectId: string) {
  const { data } = useProjectNavigation(projectId);
  return {
    data: data?.goal || null,
    isLoading: false, // The main query handles loading state
    error: null
  };
}

export function useOptimizedPageContentQuery(projectId: string) {
  const { data } = useProjectNavigation(projectId);
  return {
    data: data?.pages || [],
    isLoading: false, // The main query handles loading state
    error: null
  };
}

export function useOptimizedPageQuery(projectId: string) {
  const { data } = useProjectNavigation(projectId);
  return {
    data: data?.project || null,
    isLoading: false, // The main query handles loading state
    error: null
  };
} 